import React from "react";
import { Property } from "../../../types/search";
import PropertyCard from "./PropertyCard";
import { useTenant } from "../../../contexts/TenantContext";
import { Loader, ChevronDown, Home } from "lucide-react";
import ComplianceFooter from "./ComplianceFooter";
import { AILoadingText } from "../../common/AILoadingText";
import { EmptyStateMessage } from "../../common/EmptyStateMessage";
import { UseQueryResult } from "@tanstack/react-query";

interface MLSComplianceResponse {
  status: string;
  data: {
    mlsBoardCode: string;
    mlsLogo: string;
    complianceText: string;
  };
}

interface SearchResultsProps {
  results: Property[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  onLoadMore: () => void;
  onPropertyClick: (property: Property) => void;
  totalAvailable: number; // Total available results
  onRetry?: () => void; // Optional retry handler
  gridColumns?: 2 | 3; // Optional prop to control number of columns
  filters?: {
    bedrooms?: number;
    bathrooms?: number;
    min_price?: number;
    max_price?: number;
    waterfront?: boolean;
    pool?: boolean;
    basement?: boolean;
    garage?: boolean;
    city_view?: boolean;
    mountain_view?: boolean;
    park_view?: boolean;
    water_view?: boolean;
    sqft_min?: number;
    sqft_max?: number;
  };
  searchQuery?: string;
  timestamp?: string;
  forceShowScores?: boolean; // Added prop to force showing scores
  pageTitle?: string; // Added prop for page title
  hideGoogleSignIn?: boolean; // Added prop to hide Google sign-in
  mlsLogosMap?: Map<string, string>; // MLS logos map passed from parent
  mlsComplianceQueries?: UseQueryResult<MLSComplianceResponse, Error>[]; // MLS compliance queries passed from parent
}

export const SearchResults: React.FC<SearchResultsProps> = ({
  results,
  loading,
  error,
  hasMore,
  onLoadMore,
  onPropertyClick,
  totalAvailable,
  onRetry,
  gridColumns,
  filters,
  searchQuery,
  timestamp,
  forceShowScores = false,
  pageTitle,
  hideGoogleSignIn,
  mlsLogosMap,
  mlsComplianceQueries,
}) => {
  const { config } = useTenant();

  if (!config) return null;

  if (error) {
    return (
      <div
        className="text-center py-8"
        /* style={{ fontFamily: config.branding.fonts.primary }} */
      >
        <div className="mb-2" style={{ color: "#dc2626" }}>
          Error: {error}
        </div>
        <button
          onClick={onRetry || (() => window.location.reload())}
          className="px-4 py-2 text-white rounded-md btn-primary"
          style={{ backgroundColor: config.branding.colors.primary }}
        >
          Retry Search
        </button>
      </div>
    );
  }

  if (!loading && results.length === 0) {
    return (
      <EmptyStateMessage
        title="No properties found"
        subtitle="Try adjusting your search criteria to find more properties"
        style={{ color: config.branding.colors.text }}
      />
    );
  }

  return (
    <div
      className="relative w-full mx-auto"
      /* style={{ fontFamily: config.branding.fonts.primary }} */
    >
      {results.length > 0 && (
        <div className="mb-2">
          {loading ? (
            <AILoadingText style={{ color: config.branding.colors.primary }} />
          ) : (
            <div
              style={{ color: config.branding.colors.text }}
              className="text-base"
            >
              {pageTitle || `Found ${totalAvailable} Properties`}
              {!loading && (
                <span
                  className="ml-2"
                  style={{ color: `${config.branding.colors.text}99` }}
                >
                  (Showing{" "}
                  <span className="font-semibold">
                    {results.length.toLocaleString()}
                  </span>{" "}
                  of{" "}
                  <span className="font-semibold">
                    {totalAvailable.toLocaleString()}
                  </span>
                  )
                </span>
              )}
            </div>
          )}
        </div>
      )}

      <div
        className={`grid grid-cols-1 md:grid-cols-2 ${
          gridColumns === 2 ? "" : "lg:grid-cols-3"
        } gap-6 ${loading ? "mt-10" : ""}`}
      >
        {results.map((property, index) => (
          <div
            key={`${property.id}-${property.mlsNumber}-${index}`}
            className={loading ? "animate-pulse" : ""}
          >
            <PropertyCard
              property={property}
              onClick={onPropertyClick}
              showScore={
                forceShowScores ||
                (index < 20 &&
                  (searchQuery?.trim() !== "" ||
                    Object.keys(filters || {}).length > 0))
              }
              hideGoogleSignIn={hideGoogleSignIn}
              isFirstCard={index < 2} // Mark the first TWO cards for eager loading
              index={index} // Pass the index to identify the very first card
              mlsLogo={
                property.mlsBoardCode && mlsLogosMap
                  ? mlsLogosMap.get(property.mlsBoardCode)
                  : undefined
              }
            />
          </div>
        ))}
      </div>

      {loading && results.length === 0 && (
        <div className="flex justify-center items-center py-8">
          <AILoadingText style={{ color: config.branding.colors.primary }} />
        </div>
      )}

      {!loading && hasMore && (
        <div className="flex justify-center mt-8">
          <button
            onClick={onLoadMore}
            className="px-6 py-2 lg:px-8 lg:py-3 rounded-md transition-colors text-sm hover:opacity-80"
            style={{
              backgroundColor: config.branding.colors.primary,
              color: "#fff",
            }}
            disabled={loading}
          >
            {loading ? (
              <span className="flex items-center">
                <Loader
                  className="animate-spin h-4 w-4 mr-2"
                  style={{ color: config.branding.colors.primary }}
                />
                Loading...
              </span>
            ) : (
              <span className="flex items-center text-sm">
                Load More Properties
                <ChevronDown className="ml-2 h-5 w-5" />
              </span>
            )}
          </button>
        </div>
      )}

      {results.length > 0 && !hasMore && !loading && (
        <p className="text-center text-gray-600 mt-8 text-sm xl:text-base">
          You've reached the end of the results.
        </p>
      )}

      {/* Add Compliance Footer */}
      {results.length > 0 && (
        <ComplianceFooter
          properties={results}
          timestamp={timestamp}
          mlsComplianceQueries={mlsComplianceQueries}
        />
      )}
    </div>
  );
};

export default SearchResults;
